import React from 'react';
import { AlertOctagon, RefreshCw, LifeBuoy } from 'lucide-react';

/**
 * ErrorBoundary global de la aplicación.
 * Captura cualquier excepción no manejada en el árbol React y muestra
 * un mensaje amigable en lugar de una pantalla en blanco.
 * Loggea el error a la consola con detalle para debug en producción.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, info: null, eventLogged: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        this.setState({ info });
        console.error('[ErrorBoundary] Captured error:', error, info);

        // Best-effort: registrar en backend para visibilidad admin
        if (!this.state.eventLogged) {
            try {
                const body = JSON.stringify({
                    stage: 'abandoned',
                    last_action: 'react_error_boundary',
                    error: String(error?.message || error || 'unknown').slice(0, 500),
                    meta: {
                        component_stack: String(info?.componentStack || '').slice(0, 1000),
                        url: window.location.href,
                    },
                });
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/funnel-event`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                    keepalive: true,
                }).catch(() => { /* silent */ });
                this.setState({ eventLogged: true });
            } catch (_) { /* silent */ }
        }
    }

    handleReload = () => {
        try {
            window.location.reload();
        } catch (_) { /* silent */ }
    };

    /**
     * Limpia todo el almacenamiento local del navegador (localStorage, sessionStorage,
     * cookies de aplicación) y recarga. Útil para usuarios con datos corruptos —
     * caso típico: la misma cuenta funciona en otro navegador pero falla en Chrome.
     */
    handleHardReset = () => {
        try {
            window.localStorage.clear();
        } catch (_) { /* silent */ }
        try {
            window.sessionStorage.clear();
        } catch (_) { /* silent */ }
        try {
            // Borrar cookies del dominio actual (las marcadas HttpOnly NO se pueden
            // borrar desde JS — pero el backend las invalida al hacer login).
            document.cookie.split(';').forEach((c) => {
                const eq = c.indexOf('=');
                const name = (eq > -1 ? c.substr(0, eq) : c).trim();
                if (name) {
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                }
            });
        } catch (_) { /* silent */ }
        try {
            window.location.replace('/login');
        } catch (_) {
            window.location.reload();
        }
    };

    handleOpenSoporte = () => {
        try {
            window.dispatchEvent(
                new CustomEvent('open-soporte', {
                    detail: {
                        asunto: 'Error inesperado en la plataforma',
                        mensaje: `Ocurrió un error mientras intentaba usar la plataforma. Detalle técnico: ${this.state.error?.message || 'no disponible'}`,
                    },
                })
            );
        } catch (_) {
            this.handleReload();
        }
    };

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-slate-50 px-4"
                data-testid="error-boundary-screen"
            >
                <div className="max-w-md w-full rounded-2xl border border-rose-200 bg-white p-6 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100">
                            <AlertOctagon className="h-5 w-5 text-rose-700" />
                        </div>
                        <div>
                            <h2 className="font-heading text-lg font-bold text-slate-900">
                                Algo no funcionó como esperábamos
                            </h2>
                            <p className="text-xs text-slate-500">
                                Tu trabajo guardado está seguro. Esto fue un error de pantalla.
                            </p>
                        </div>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                        Intenta recargar la página. Si el problema persiste, escríbenos desde el botón
                        de soporte y revisaremos tu caso lo antes posible.
                    </p>
                    {this.state.error?.message && (
                        <details className="mt-3 text-[10px] text-slate-400">
                            <summary className="cursor-pointer hover:text-slate-600">
                                Detalle técnico (para soporte)
                            </summary>
                            <pre className="mt-2 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-2 max-h-32 overflow-auto">
                                {String(this.state.error.message)}
                            </pre>
                        </details>
                    )}
                    <div className="mt-5 flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={this.handleReload}
                            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
                            data-testid="error-boundary-reload"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Recargar página
                        </button>
                        <button
                            type="button"
                            onClick={this.handleOpenSoporte}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                            data-testid="error-boundary-soporte"
                        >
                            <LifeBuoy className="h-4 w-4" />
                            Contactar soporte
                        </button>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={this.handleHardReset}
                            className="text-xs text-slate-500 hover:text-rose-700 underline-offset-2 hover:underline"
                            data-testid="error-boundary-hard-reset"
                            title="Limpia caché, cookies y sesión local del navegador"
                        >
                            ¿Sigue sin funcionar? Limpiar datos del sitio y volver a entrar
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
